import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Trophy, Calendar, Users, Star, Target, Award, Swords, 
  ChevronRight, Crown, Key, Waves, TreePine, Castle, Loader2,
  ArrowUp, ArrowDown, Minus, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HOUSE_ICONS = { key: Key, waves: Waves, trees: TreePine, tower: Castle };
const SKILL_LABELS = { putting: 'Putting', chipping: 'Chipping', fer: 'Fers', drive: 'Drive' };
const MEDAL_COLORS = ['#d97706', '#9ca3af', '#b45309'];

const RANK_VALUES = { 'S+': 15, 'S': 10, 'A': 8, 'B': 6, 'C': 3, 'D': 1 };
const RANK_COLORS = { 'S+': '#d97706', 'S': '#ea580c', 'A': '#16a34a', 'B': '#2563eb', 'C': '#6b7280', 'D': '#9ca3af' };

const HouseIcon = ({ icon, color, size = 20 }) => {
  const Ic = HOUSE_ICONS[icon] || Shield;
  return <Ic size={size} style={{ color }} />;
};

// Sub-views
const VIEWS = { HOME: 'home', EVENTS: 'events', HOUSES: 'houses', TOP5: 'top5', PREDICTIONS: 'predictions', LEADERBOARD: 'leaderboard' };

export default function GolfSchoolPortal({ token, clientId, clientName }) {
  const [view, setView] = useState(VIEWS.HOME);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [top5, setTop5] = useState({});
  const [predictionDialog, setPredictionDialog] = useState(null);
  const [predSubmitting, setPredSubmitting] = useState(false);
  const [predForm, setPredForm] = useState([]);
  const [rankPredictions, setRankPredictions] = useState({});
  const [selectedHouseId, setSelectedHouseId] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, playersRes, top5Res] = await Promise.all([
        fetch(`${API_URL}/api/golf-school/dashboard`, { headers }),
        fetch(`${API_URL}/api/golf-school/players`, { headers }),
        fetch(`${API_URL}/api/golf-school/top5`, { headers }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (playersRes.ok) setAllPlayers(await playersRes.json());
      if (top5Res.ok) setTop5(await top5Res.json());
    } catch (e) {
      console.error('Golf school load error:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const openPrediction = (ev) => {
    const houseList = dashboard?.houses || [];
    const existing = dashboard?.my_prediction;
    const existingRanking = existing?.predicted_ranking || [];
    const existingRanks = existing?.rank_predictions || {};
    const form = houseList.map((h, i) => {
      const found = existingRanking.find(e => e.house_id === h.id);
      return { house_id: h.id, house_name: h.name, position: found?.position || (i + 1) };
    });
    form.sort((a, b) => a.position - b.position);
    setPredForm(form);
    setRankPredictions(existingRanks);
    setPredictionDialog(ev);
  };

  const submitPrediction = async () => {
    if (!predictionDialog) return;
    setPredSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/golf-school/predictions/${predictionDialog.id}`, {
        method: 'POST', headers,
        body: JSON.stringify({ predicted_ranking: predForm, rank_predictions: rankPredictions, player_name: clientName })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Pronostic enregistré !');
      setPredictionDialog(null);
      loadDashboard();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPredSubmitting(false);
    }
  };

  const movePred = (idx, dir) => {
    const newForm = [...predForm];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newForm.length) return;
    [newForm[idx], newForm[swapIdx]] = [newForm[swapIdx], newForm[idx]];
    newForm.forEach((item, i) => { item.position = i + 1; });
    setPredForm(newForm);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-neutral-400" size={32} />
      </div>
    );
  }

  const houses = dashboard?.houses || [];
  const events = dashboard?.events || [];
  const nextEvent = dashboard?.next_event;
  const lastEvent = dashboard?.last_event;
  const predLeaderboard = dashboard?.predictions_leaderboard || [];
  const myPrediction = dashboard?.my_prediction;
  const myPlayer = dashboard?.my_player;
  const individualLeaderboard = dashboard?.individual_leaderboard || [];

  // ========== HOME VIEW ==========
  const HomeView = () => {
    const selectedHouse = selectedHouseId ? houses.find(h => h.id === selectedHouseId) : null;
    const selectedHousePlayers = selectedHouseId ? allPlayers.filter(p => p.house_id === selectedHouseId) : [];

    return (
    <div className="space-y-4">
      {/* Mon profil joueur — compact */}
      {myPlayer && (
        <Card className="overflow-hidden" style={{ borderColor: `${myPlayer.house_color}30` }} data-testid="my-player-card">
          <div className="h-1" style={{ backgroundColor: myPlayer.house_color }} />
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${myPlayer.house_color}12` }}>
                <HouseIcon icon={myPlayer.house_icon} color={myPlayer.house_color} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-sm">{myPlayer.player_name}</h2>
                <p className="text-[11px] text-neutral-500">{myPlayer.house_name} — {myPlayer.total_points} pts</p>
                {myPlayer.history?.length > 0 && (
                  <p className="text-[9px] text-neutral-400 italic">Dernier événement : {myPlayer.history[0]?.event_name}</p>
                )}
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg" style={{ backgroundColor: `${RANK_COLORS[myPlayer.rank] || '#666'}15`, color: RANK_COLORS[myPlayer.rank] || '#666' }}>
                {myPlayer.rank}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5 mt-2">
              {Object.entries(SKILL_LABELS).map(([key, label]) => (
                <div key={key} className="text-center py-1 bg-neutral-50 rounded">
                  <div className="text-[9px] text-neutral-400">{label}</div>
                  <div className="font-bold text-xs" style={{ color: RANK_COLORS[myPlayer.skills?.[key]] || '#ccc' }}>
                    {myPlayer.skills?.[key] || '—'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classement des maisons */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-base text-neutral-800">Classement des Maisons</h2>
          <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => setView(VIEWS.HOUSES)} data-testid="view-houses-btn">
            Voir tout <ChevronRight size={14} />
          </Button>
        </div>
        <div className="space-y-2">
          {houses.map((h, idx) => (
            <div key={h.id}>
              <button
                onClick={() => setSelectedHouseId(selectedHouseId === h.id ? null : h.id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer hover:shadow-sm"
                style={{ backgroundColor: `${h.color}08`, border: `1px solid ${selectedHouseId === h.id ? h.color : `${h.color}20`}` }}
                data-testid={`house-rank-${idx}`}
              >
              <div className="w-8 text-center">
                {idx < 3 ? (
                  <Crown size={18} style={{ color: MEDAL_COLORS[idx] }} />
                ) : (
                  <span className="text-sm font-bold text-neutral-400">{idx + 1}</span>
                )}
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${h.color}15` }}>
                <HouseIcon icon={h.icon} color={h.color} size={22} />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="font-bold text-sm truncate">{h.name}</div>
                <div className="text-[11px] text-neutral-500">{h.player_count} joueur{h.player_count !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black" style={{ color: h.color }}>{h.total_points}</div>
                <div className="text-[10px] text-neutral-500">pts</div>
              </div>
              <ChevronDown size={16} className={`text-neutral-400 transition-transform ${selectedHouseId === h.id ? 'rotate-180' : ''}`} />
              </button>
              {/* Liste des joueurs de la maison */}
              {selectedHouseId === h.id && (
                <div className="ml-8 mt-1 mb-2 space-y-1 animate-in slide-in-from-top-2">
                  {allPlayers.filter(p => p.house_id === h.id).map(p => (
                    <div key={p.id} className="flex items-center gap-1.5 py-1 px-2.5 bg-white rounded border text-xs">
                      <span className="flex-1 font-medium truncate">{p.player_name}</span>
                      <span className="text-[9px] text-neutral-400">{p.total_points || 0}</span>
                      <div className="w-6 h-6 rounded flex items-center justify-center font-bold text-[10px]" style={{ backgroundColor: `${RANK_COLORS[p.rank] || '#ccc'}15`, color: RANK_COLORS[p.rank] || '#ccc' }}>
                        {p.rank || '—'}
                      </div>
                    </div>
                  ))}
                  {allPlayers.filter(p => p.house_id === h.id).length === 0 && (
                    <div className="text-xs text-neutral-400 text-center py-2">Aucun joueur</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Prochain événement */}
      {nextEvent && (
        <Card className="overflow-hidden border-amber-200 bg-amber-50/50" data-testid="next-event-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={16} className="text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Prochain événement</span>
            </div>
            <h3 className="font-bold text-base mb-1">{nextEvent.name}</h3>
            <p className="text-sm text-neutral-600">
              {nextEvent.date ? new Date(nextEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
              {nextEvent.location ? ` - ${nextEvent.location}` : ''}
            </p>
            {nextEvent.predictions_open && (
              <div className="mt-3">
                {myPrediction ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check size={12} className="mr-1" /> Pronostic enregistré
                  </Badge>
                ) : (
                  <Button size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700" onClick={() => openPrediction(nextEvent)} data-testid="make-prediction-btn">
                    <Target size={14} /> Faire mon pronostic
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dernier résultat */}
      {lastEvent && lastEvent.house_results?.length > 0 && (
        <Card className="overflow-hidden" data-testid="last-event-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={16} className="text-amber-500" />
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Dernier résultat</span>
            </div>
            <h3 className="font-semibold text-sm mb-2">{lastEvent.name}</h3>
            <div className="flex flex-wrap gap-2">
              {lastEvent.house_results.sort((a, b) => a.position - b.position).map((r, i) => {
                const h = houses.find(h => h.id === r.house_id);
                return (
                  <Badge key={r.house_id} variant="outline" className="text-xs py-1" style={{ borderColor: h?.color, color: h?.color }}>
                    {i < 3 ? <Crown size={10} className="mr-1" style={{ color: MEDAL_COLORS[i] }} /> : null}
                    #{r.position} {r.house_name} ({r.points}pts)
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classement pronostics */}
      {predLeaderboard.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-base text-neutral-800">Top Pronostiqueurs</h2>
          </div>
          <div className="space-y-1.5">
            {predLeaderboard.slice(0, 5).map((p, i) => (
              <div key={p.client_id} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border">
                <span className="w-6 text-center font-bold text-sm" style={{ color: i < 3 ? MEDAL_COLORS[i] : '#9ca3af' }}>
                  {i + 1}
                </span>
                <div className="flex-1 text-sm font-medium truncate">{p.player_name}</div>
                <Badge variant="secondary" className="text-xs font-mono">{p.total_score} pts</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick nav */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { view: VIEWS.LEADERBOARD, label: 'Classement', icon: Trophy, color: '#d97706' },
          { view: VIEWS.EVENTS, label: 'Événements', icon: Calendar, color: '#2563eb' },
          { view: VIEWS.HOUSES, label: 'Maisons', icon: Users, color: '#16a34a' },
          { view: VIEWS.PREDICTIONS, label: 'Pronostics', icon: Target, color: '#dc2626' },
        ].map(item => (
          <button key={item.view} onClick={() => setView(item.view)} className="p-4 rounded-xl border bg-white hover:shadow-md transition-all text-left" data-testid={`nav-${item.view}`}>
            <item.icon size={20} style={{ color: item.color }} />
            <div className="font-semibold text-sm mt-2">{item.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
  };

  // ========== EVENTS VIEW ==========
  const EventsView = () => {
    const upcoming = events.filter(e => e.status === 'upcoming');
    const completed = events.filter(e => e.status === 'completed');
    return (
      <div className="space-y-5">
        {upcoming.length > 0 && (
          <div>
            <h3 className="font-bold text-sm text-neutral-800 mb-3">A venir</h3>
            <div className="space-y-2">
              {upcoming.map(ev => (
                <Card key={ev.id} className="overflow-hidden" data-testid={`event-upcoming-${ev.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-sm">{ev.name}</h4>
                        <p className="text-xs text-neutral-500">
                          {ev.date ? new Date(ev.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                        </p>
                        {ev.description && <p className="text-xs text-neutral-600 mt-1">{ev.description}</p>}
                      </div>
                      {ev.predictions_open && (
                        <Button size="sm" variant="outline" className="text-xs gap-1 flex-shrink-0" onClick={() => openPrediction(ev)}>
                          <Target size={12} /> Pronostiquer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {completed.length > 0 && (
          <div>
            <h3 className="font-bold text-sm text-neutral-800 mb-3">Terminés</h3>
            <div className="space-y-2">
              {completed.map(ev => (
                <Card key={ev.id} className="overflow-hidden" data-testid={`event-completed-${ev.id}`}>
                  <CardContent className="p-3">
                    <h4 className="font-bold text-sm">{ev.name}</h4>
                    <p className="text-xs text-neutral-500 mb-2">
                      {ev.date ? new Date(ev.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : ''}
                    </p>
                    {ev.house_results?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {ev.house_results.sort((a, b) => a.position - b.position).map((r, i) => {
                          const h = houses.find(h => h.id === r.house_id);
                          return (
                            <Badge key={r.house_id} variant="outline" className="text-[11px]" style={{ borderColor: h?.color, color: h?.color }}>
                              #{r.position} {r.house_name} ({r.points}pts)
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {events.length === 0 && (
          <div className="text-center py-12 text-neutral-500 text-sm">Aucun événement pour le moment.</div>
        )}
      </div>
    );
  };

  // ========== HOUSES VIEW ==========
  const HousesView = () => (
    <div className="space-y-4">
      {houses.map((h, idx) => {
        const housePlayers = allPlayers.filter(p => p.house_id === h.id);
        return (
          <Card key={h.id} className="overflow-hidden" data-testid={`house-detail-${idx}`}>
            <div className="h-1.5" style={{ backgroundColor: h.color }} />
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${h.color}15` }}>
                  <HouseIcon icon={h.icon} color={h.color} size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-base">{h.name}</h3>
                  <p className="text-xs text-neutral-500">{housePlayers.length} joueur{housePlayers.length !== 1 ? 's' : ''} - {h.total_points} pts</p>
                </div>
                <div className="text-2xl font-black" style={{ color: h.color }}>#{idx + 1}</div>
              </div>
              {housePlayers.length > 0 && (
                <div className="space-y-1.5">
                  {housePlayers.map(p => (
                    <div key={p.id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg">
                      <span className="text-sm font-medium flex-1 truncate">{p.player_name}</span>
                      <Badge variant="outline" className="text-[10px] font-mono" style={{ borderColor: h.color, color: h.color }}>{p.rank}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // ========== TOP 5 VIEW ==========
  const Top5View = () => {
    const SKILL_ICONS = { putting: Target, chipping: Star, fer: Swords, drive: Award };
    return (
      <div className="space-y-4">
        {Object.entries(SKILL_LABELS).map(([skill, label]) => {
          const Icon = SKILL_ICONS[skill] || Star;
          const list = top5?.[skill] || [];
          return (
            <Card key={skill} data-testid={`top5-client-${skill}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Icon size={16} className="text-amber-500" /> Top 5 {label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {list.length === 0 ? (
                  <p className="text-xs text-neutral-500 text-center py-3">Pas encore de classement</p>
                ) : (
                  <div className="space-y-1.5">
                    {list.map((p, i) => {
                      const h = houses.find(h => h.id === p.house_id);
                      return (
                        <div key={p.id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg">
                          <span className="w-5 text-center font-bold text-xs" style={{ color: i < 3 ? MEDAL_COLORS[i] : '#9ca3af' }}>
                            {i + 1}
                          </span>
                          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${h?.color || '#666'}15` }}>
                            <HouseIcon icon={h?.icon} color={h?.color || '#666'} size={12} />
                          </div>
                          <span className="flex-1 text-sm font-medium truncate">{p.player_name}</span>
                          <Badge variant="outline" className="text-[10px] font-mono" style={{ borderColor: h?.color, color: h?.color }}>
                            {p.skill_rank}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // ========== PREDICTIONS VIEW ==========
  const PredictionsView = () => (
    <div className="space-y-5">
      {/* Prochain événement ouvert */}
      {nextEvent?.predictions_open && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="prediction-open-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target size={16} className="text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Pronostic ouvert</span>
            </div>
            <h3 className="font-bold text-base mb-1">{nextEvent.name}</h3>
            <p className="text-sm text-neutral-600 mb-3">
              {nextEvent.date ? new Date(nextEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
            </p>
            {myPrediction ? (
              <div>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 mb-2">
                  <Check size={12} className="mr-1" /> Pronostic enregistré
                </Badge>
                <div className="flex flex-wrap gap-1.5">
                  {(myPrediction.predicted_ranking || []).sort((a, b) => a.position - b.position).map(r => {
                    const h = houses.find(h => h.id === r.house_id);
                    return (
                      <Badge key={r.house_id} variant="secondary" className="text-[11px]">
                        #{r.position} {r.house_name}
                      </Badge>
                    );
                  })}
                </div>
                <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => openPrediction(nextEvent)}>
                  Modifier mon pronostic
                </Button>
              </div>
            ) : (
              <Button size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700" onClick={() => openPrediction(nextEvent)}>
                <Target size={14} /> Faire mon pronostic
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Classement */}
      <div>
        <h3 className="font-bold text-sm text-neutral-800 mb-3">Classement des pronostiqueurs</h3>
        {predLeaderboard.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 text-sm">Pas encore de classement.</div>
        ) : (
          <div className="space-y-1.5">
            {predLeaderboard.map((p, i) => (
              <div key={p.client_id} className="flex items-center gap-3 p-3 bg-white rounded-lg border" data-testid={`pred-rank-${i}`}>
                <span className="w-6 text-center font-bold text-sm" style={{ color: i < 3 ? MEDAL_COLORS[i] : '#9ca3af' }}>
                  {i + 1}
                </span>
                <div className="flex-1 text-sm font-medium truncate">{p.player_name}</div>
                <div className="text-right">
                  <div className="text-sm font-bold">{p.total_score} pts</div>
                  <div className="text-[10px] text-neutral-500">{p.count} pronostic{p.count !== 1 ? 's' : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ========== LEADERBOARD VIEW (Classement individuel global) ==========
  const LeaderboardView = () => (
    <div className="space-y-3">
      <p className="text-xs text-neutral-500 mb-2">Classement basé sur les points cumulés (somme des valeurs de rang sur les 4 skills).</p>
      {individualLeaderboard.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 text-sm">Pas encore de classement individuel.</div>
      ) : (
        individualLeaderboard.map((p, i) => {
          const h = houses.find(h => h.id === p.house_id);
          const isMe = myPlayer && p.id === myPlayer.id;
          return (
            <div
              key={p.id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${isMe ? 'ring-2 ring-amber-400 bg-amber-50/50' : 'bg-white'}`}
              data-testid={`leaderboard-row-${i}`}
            >
              <span className="w-7 text-center font-black text-sm" style={{ color: i < 3 ? MEDAL_COLORS[i] : '#9ca3af' }}>
                {i < 3 ? <Crown size={18} style={{ color: MEDAL_COLORS[i] }} /> : `${i + 1}`}
              </span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${h?.color || '#666'}12` }}>
                <HouseIcon icon={h?.icon} color={h?.color || '#666'} size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {p.player_name} {isMe && <span className="text-amber-600 text-xs">(toi)</span>}
                </div>
                <div className="text-[10px] text-neutral-500">{h?.name || ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-center">
                  <div className="font-bold text-sm" style={{ color: h?.color || '#666' }}>{p.total_points}</div>
                  <div className="text-[9px] text-neutral-500">pts</div>
                </div>
                <Badge className="text-xs font-bold w-8 justify-center" style={{ backgroundColor: `${RANK_COLORS[p.rank] || '#666'}15`, color: RANK_COLORS[p.rank] || '#666', border: `1px solid ${RANK_COLORS[p.rank] || '#666'}30` }}>
                  {p.rank}
                </Badge>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  // ========== MAIN RENDER ==========
  const viewConfig = {
    [VIEWS.HOME]: { title: 'École de Golf', component: HomeView },
    [VIEWS.EVENTS]: { title: 'Événements', component: EventsView },
    [VIEWS.HOUSES]: { title: 'Les Maisons', component: HousesView },
    [VIEWS.TOP5]: { title: 'Top 5', component: Top5View },
    [VIEWS.PREDICTIONS]: { title: 'Pronostics', component: PredictionsView },
    [VIEWS.LEADERBOARD]: { title: 'Classement Individuel', component: LeaderboardView },
  };

  const current = viewConfig[view] || viewConfig[VIEWS.HOME];
  const CurrentComponent = current.component;

  return (
    <div className="max-w-2xl mx-auto" data-testid="golf-school-portal">
      {/* Header */}
      {view !== VIEWS.HOME && (
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => setView(VIEWS.HOME)} className="gap-1 text-xs" data-testid="back-to-home">
            <ChevronRight size={14} className="rotate-180" /> Retour
          </Button>
          <h2 className="font-bold text-base">{current.title}</h2>
        </div>
      )}

      <CurrentComponent />

      {/* Prediction Dialog */}
      <Dialog open={!!predictionDialog} onOpenChange={v => { if (!v) setPredictionDialog(null); }}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target size={18} className="text-amber-600" /> Mon pronostic
            </DialogTitle>
            <DialogDescription>
              <strong>{predictionDialog?.name}</strong> — 4 pts max (1 classement + 1 rangs + 2 combo)
            </DialogDescription>
          </DialogHeader>

          {/* 1. Classement des maisons */}
          <div>
            <p className="text-xs font-semibold text-neutral-600 mb-2">Classement des maisons (1 pt)</p>
            <div className="space-y-1.5">
              {predForm.map((item, idx) => {
                const h = houses.find(h => h.id === item.house_id);
                return (
                  <div key={item.house_id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg">
                    <span className="w-5 text-center font-bold text-xs" style={{ color: idx < 3 ? MEDAL_COLORS[idx] : '#9ca3af' }}>
                      {idx + 1}
                    </span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${h?.color}15` }}>
                      <HouseIcon icon={h?.icon} color={h?.color} size={14} />
                    </div>
                    <span className="flex-1 text-xs font-semibold">{item.house_name}</span>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => movePred(idx, -1)} disabled={idx === 0}>
                        <ChevronUp size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => movePred(idx, 1)} disabled={idx === predForm.length - 1}>
                        <ChevronDown size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 2. Meilleur rang par maison */}
          <div>
            <p className="text-xs font-semibold text-neutral-600 mb-2">Meilleur rang par maison (1 pt)</p>
            <div className="space-y-1.5">
              {houses.map(h => (
                <div key={h.id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${h.color}15` }}>
                    <HouseIcon icon={h.icon} color={h.color} size={14} />
                  </div>
                  <span className="flex-1 text-xs font-semibold truncate">{h.name}</span>
                  <div className="flex gap-0.5">
                    {['D', 'C', 'B', 'A', 'S', 'S+'].map(r => (
                      <button
                        key={r}
                        onClick={() => setRankPredictions(prev => ({ ...prev, [h.id]: r }))}
                        className="min-w-[26px] h-7 rounded text-[9px] font-bold transition-all px-0.5"
                        style={{
                          backgroundColor: rankPredictions[h.id] === r ? `${RANK_COLORS[r]}20` : 'transparent',
                          color: rankPredictions[h.id] === r ? RANK_COLORS[r] : '#ccc',
                          border: rankPredictions[h.id] === r ? `2px solid ${RANK_COLORS[r]}` : '1px solid #e5e5e5',
                        }}
                        data-testid={`rank-pred-${h.id}-${r}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPredictionDialog(null)}>Annuler</Button>
            <Button onClick={submitPrediction} disabled={predSubmitting} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-prediction-btn">
              {predSubmitting && <Loader2 className="animate-spin mr-2" size={16} />}
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
